/*
 * Provides hill finding functions findAscents(points: TrackPoint[]) and findDescents(points: TrackPoint[])
 *
 * Walks through points from left to right, tracking sections with elevation strictly above (or below for descents)
 * a prior point. Once a section is found that exceeds MIN_DIST_TO_CONSIDER, it is required that the avg grade
 * exceeds MIN_ABS_GRADE_FOR_HILL.  Once that requirement is no longer met, the hill is adjusted. Adjusting
 * involves alternating between start and end point, pulling them away from the middle to enlarge the hill, as long
 * as the average grade constraint stays met.
 *
 * Once all hills are found, overlapping hills are merged and hills with a gap between them less than
 * MIN_DIST_TO_SAVE are merged if the resulting hill meets our constraints.
 */

// temp in here because the real ver. uses bs global vars
function buildSplitObject (firstI, lastI, deltaE) {
  return { first_i: firstI, last_i: lastI, delta_e: deltaE }
}

const MIN_ABS_GRADE_FOR_HILL = 0.03 // nothing less than avg grade of 3% gets saved
const MIN_DIST_TO_SAVE = 1000 // don't bother saving any hills shorter than this
const MIN_DIST_TO_CONSIDER = 300 // once hills are > this length, they must exceed the minimum grade

// FIXME get peaks from the hill finder code not just naively grab highest point
export function findPeaks (points) {
  if (points.length === 0) {
    return
  }
  let max = 0
  points.forEach((point, i) => {
    if (point.ele > points[max].ele) {
      max = i
    }
  })
  return [max]
}

export function findAscents (points) {
  return findHills(points)
}

export function findDescents (points) {
  return findHills(points, -1)
}

// sign=1 for climbs, -1 for descents
function findHills (points, sign = 1) {
  const hf = hillFinderState(points, sign)
  return mergeHills(hf, findAllHills(hf))
}

// one pass through points to find all hills and independently adjust endpoints
function findAllHills (hf) {
  reset(hf)
  let hills = []
  // reset to first point with ele
  hf.i = hf.points.findIndex(p => Number.isFinite(p.ele))
  if (hf.i === -1) {
    // all ele data in hf is garbage
    return []
  }
  while (++hf.i < hf.points.length) {
    const hill = expand(hf)
    if (hill) hills.push(hill)
  }
  const hill = submitHill(hf)
  if (hill) hills.push(hill)
  return hills
}

// merge overlapping hills or hills that are close and when combined still meet grade criteria
function mergeHills (hf, hills) {
  let lastHill
  let updatedHills = hills
  do {
    hills = updatedHills
    lastHill = null
    updatedHills = []
    hills = hills.sort((a, b) => a.first_i - b.first_i)
    hills.forEach((hill) => {
      if (!lastHill) {
        lastHill = hill
        return
      }
      if (lastHill.last_i < hill.first_i) {
        let distSeparating = hf.points[hill.first_i].distance - hf.points[lastHill.last_i].distance
        let distance = hf.points[hill.last_i].distance - hf.points[lastHill.first_i].distance
        let eleDelta = hf.sign * (hf.points[hill.last_i].ele - hf.points[lastHill.first_i].ele)
        let avgGrade = eleDelta / distance
        if (distSeparating < MIN_DIST_TO_SAVE && avgGrade > MIN_ABS_GRADE_FOR_HILL && testAntiPeakForMergeWithGap(hf, lastHill, hill)) {
          lastHill = mergeAndExpand(hf, lastHill, hill)
        } else {
          updatedHills.push(lastHill)
          lastHill = hill
        }
      } else {
        lastHill = mergeAndExpand(hf, lastHill, hill)
      }
    })
    if (lastHill) {
      updatedHills.push(lastHill)
    }
  } while (hills.length > updatedHills.length)
  return hills
}

// given two hills that are not overlapping, close enough to merge, and having an acceptable combined avg grade
// this is used to test that there isn't a big surprise (hill) between that would make merging them violage our antipeak rule
function testAntiPeakForMergeWithGap (hf, leftHill, rightHill) {
  const { points, sign } = hf
  const peak = points[leftHill.last_i].ele
  let antiPeak = peak
  for (let i = leftHill.last_i; i < rightHill.first_i; i++) {
    if (sign * points[i].ele < antiPeak) {
      antiPeak = points[i].ele
    }
  }
  return Math.abs(peak - antiPeak) / 2 < Math.abs(points[rightHill.last_i].ele - peak)
}

// helper for mergeHills - take union and adjust endpoints of new hill
function mergeAndExpand (hf, leftHill, rightHill) {
  if (leftHill.last_i >= rightHill.last_i) {
    // rightHill is entirely inside leftHill
    return leftHill
  }
  hf.i = rightHill.last_i
  reset(hf)
  hf.firstI = leftHill.first_i
  adjust(hf)
  const { points } = hf
  const deltaE = points[hf.peakI].ele - points[hf.firstI].ele
  return buildSplitObject(hf.firstI, hf.peakI, deltaE)
}

// get an object that holds the state required to find & adjust hills.
function hillFinderState (points, sign = 1) {
  return {
    points, // TrackPoint[], we find the hills in it
    sign, // 1 | -1, used as multiplier to find ascents/descents
    //
    // hill finding state
    i: 0, // iterator position
    firstI: 0, // first point when finding a hill
    peakI: 0, // highest/lowest accepted point when finding ascent/descent
    antiPeakI: 0, // lowest/highest point seen since peakI when finding ascent/descent, used to qualify false peaks
    adjusting: false // false=looking for hills, true=adjusting endpoints of a found hill
  }
}

// abort current hill finding, which means resetting state or halting an endpoint adjustment
function reset (hf) {
  if (hf.adjusting) {
    hf.adjusting = false
  } else {
    hf.firstI = hf.peakI = hf.antiPeakI = hf.i
  }
}

// make the hill as large as possible by expanding endpoints until stationary
function adjust (hf) {
  let hillFirstI = hf.firstI
  let hillLastI = hf.peakI
  let updatedHillFirstI = hillFirstI
  let updatedHillLastI = hillLastI

  do {
    hillFirstI = updatedHillFirstI
    hillLastI = updatedHillLastI

    // adjust the start point by inverting the sign, swapping start/end points, and iterating backward
    hf.sign = -hf.sign
    hf.i = hillFirstI
    reset(hf)
    hf.firstI = hillLastI
    hf.adjusting = true
    while (hf.adjusting && --hf.i >= 0) {
      expand(hf)
    }
    hf.adjusting = false
    updatedHillFirstI = hf.peakI

    // and adjust the end point forward
    hf.sign = -hf.sign
    hf.i = hillLastI
    reset(hf)
    hf.firstI = updatedHillFirstI
    hf.adjusting = true
    while (hf.adjusting && ++hf.i < hf.points.length) {
      expand(hf)
    }
    hf.adjusting = false
    updatedHillLastI = hf.peakI
  } while (updatedHillFirstI < hillFirstI || updatedHillLastI > hillLastI)

  hf.i = hf.peakI = hillLastI
}

// we think we've got a hill, adjust endpoints and check criteria before returning it
function submitHill (hf) {
  if (hf.firstI === hf.peakI) return // zero length hill
  if (hf.adjusting) {
    hf.adjusting = false
    return
  }

  adjust(hf)

  const { points, sign, firstI, peakI } = hf
  reset(hf)

  const distance = points[peakI].distance - points[firstI].distance
  const deltaE = points[peakI].ele - points[firstI].ele
  const avgGrade = sign * deltaE / distance

  if (distance > MIN_DIST_TO_SAVE && avgGrade > MIN_ABS_GRADE_FOR_HILL) {
    return buildSplitObject(firstI, peakI, deltaE)
  }
}

// try to find the next peakI (endpoint of a hill)
// some conditions result in giving up on the current hill (reset), or submitting it for further adjustment & consideration
function expand (hf) {
  if (!Number.isFinite(hf.points[hf.i].ele)) return

  const { points, sign, i, firstI, peakI, antiPeakI } = hf
  const pt = points[i]
  const first = points[firstI]
  const eleDelta = sign * (pt.ele - first.ele)

  // if we're at or below our initial point, reset
  if (eleDelta <= 0) {
    reset(hf)
    return
  }

  const peak = points[peakI]
  const distance = Math.abs(pt.distance - first.distance)
  const distToPeak = Math.abs(peak.distance - first.distance)
  const avgGrade = eleDelta / distance
  const avgGradeToPeak = eleDelta / distToPeak

  // if we've exceeded MIN_DIST_TO_CONSIDER start requiring our overall segment exceeds the MIN_ABS_GRADE_FOR_HILL
  if (distance > MIN_DIST_TO_CONSIDER && avgGrade < MIN_ABS_GRADE_FOR_HILL) {
    if (distToPeak > 0 && avgGradeToPeak > MIN_ABS_GRADE_FOR_HILL) {
      const hill = submitHill(hf)
      if (hill) return hill
    } else {
      reset(hf)
      return
    }
  }

  // we get to continue expanding. update peak/antiPeak if relevant
  const antiPeak = points[antiPeakI]
  // don't update our peak unless:
  // - it's a peak (higher than previous peak)
  // - it has 1.5x climbed out of whatever hole was dug since the last peak (the antiPeak)
  // - the grade between the old peak and new peak is at least 1% - this prevents hills extending into flat areas
  const peakToNewPeakGrade = sign * (pt.ele - peak.ele) / Math.abs(pt.distance - peak.distance)
  if (sign * pt.ele > sign * peak.ele &&
     Math.abs(peak.ele - antiPeak.ele) / 2 < Math.abs(pt.ele - peak.ele) &&
     peakToNewPeakGrade > 0.01) {
    hf.peakI = i
    hf.antiPeakI = i
  } else if (sign * pt.ele < sign * antiPeak.ele) {
    // current point is the furthest away from the peak we've seen, remember it because the next peak has to offset it to be used
    hf.antiPeakI = i
  }
}
